import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Bike, ChevronDown, SlidersHorizontal, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import BikeCard from "@/components/site/BikeCard";
import ScrollCue from "@/components/site/ScrollCue";
import { useSettings } from "@/contexts/SettingsContext";
import { buildSmartChips, presetKey, presetToParams } from "@/lib/homeFilters";
import { addToCompare, getCompareIds } from "@/lib/compare";
import { selectOption } from "@/lib/selectOption";

const BUDGETS = [
    { value: "", label: "Any", price_min: "", price_max: "" },
    { value: "u1", label: "Under ₹1L", price_min: "", price_max: "100000" },
    { value: "1-2", label: "₹1L–₹2L", price_min: "100000", price_max: "200000" },
    { value: "2-3", label: "₹2L–₹3L", price_min: "200000", price_max: "300000" },
    { value: "3p", label: "₹3L+", price_min: "300000", price_max: "" },
];

const SORTS = [
    { value: "popularity", label: "Popular" },
    { value: "price_asc", label: "Low to high" },
    { value: "price_desc", label: "High to low" },
    { value: "rating", label: "Rated" },
    { value: "newest", label: "New" },
];

const STORAGE_KEY = "servall_home_filter";

const Chip = ({ active, onClick, label, count, testId }) => (
    <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className={`snap-start inline-flex items-center gap-1 whitespace-nowrap rounded-full h-9 px-3.5 text-[13px] font-medium border transition-colors shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E31837] focus-visible:ring-offset-1 ${
            active
                ? "bg-[#E31837] border-[#E31837] text-white"
                : "bg-[#F4F6F8] border-transparent text-[#0A2540] hover:bg-[#fff5f5]"
        }`}
    >
        {label}
        {count != null && (
            <span className={`text-[10px] tabular-nums ${active ? "text-white/70" : "text-[#98a0aa]"}`}>{count}</span>
        )}
    </button>
);

const HomeBikeFinder = () => {
    const { pages } = useSettings();
    const [meta, setMeta] = useState({});
    const [category, setCategory] = useState("");
    const [fuel, setFuel] = useState("");
    const [brand, setBrand] = useState("");
    const [budget, setBudget] = useState("");
    const [sort, setSort] = useState("popularity");
    const [featuredOnly, setFeaturedOnly] = useState(false);
    const [activeKey, setActiveKey] = useState("featured");
    const [adjustOpen, setAdjustOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [compareIds, setCompareIds] = useState(getCompareIds);

    const cmsFilters = pages?.home_quick_filters;
    const smartOn = pages?.home_smart_brand_filters !== false;

    const chips = useMemo(
        () => buildSmartChips(cmsFilters?.length ? cmsFilters : null, meta, { max: 8, smartBrands: smartOn }),
        [cmsFilters, meta, smartOn],
    );

    const budgetMeta = BUDGETS.find((b) => b.value === budget) || BUDGETS[0];

    useEffect(() => {
        const sync = () => setCompareIds(getCompareIds());
        window.addEventListener("compare-updated", sync);
        return () => window.removeEventListener("compare-updated", sync);
    }, []);

    useEffect(() => {
        api.get("/bikes/meta").then((r) => setMeta(r.data || {})).catch(() => setMeta({}));
    }, []);

    useEffect(() => {
        try {
            const saved = sessionStorage.getItem(STORAGE_KEY);
            if (saved && chips.length) {
                const match = chips.find((c) => (c.id || presetKey(c)) === saved);
                if (match) applyPreset(match, false);
            }
        } catch { /* ignore */ }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chips.length > 0]);

    useEffect(() => {
        setLoading(true);
        const q = new URLSearchParams();
        if (category) q.set("category", category);
        if (fuel) q.set("fuel_type", fuel);
        if (brand) q.set("brand", brand);
        if (budgetMeta.price_min) q.set("price_min", budgetMeta.price_min);
        if (budgetMeta.price_max) q.set("price_max", budgetMeta.price_max);
        q.set("sort", sort);
        q.set("limit", "12");
        q.set("is_upcoming", "false");
        if (featuredOnly) q.set("is_featured", "true");
        else if (!category && !fuel && !brand && !budget) q.set("is_featured", "true");

        api.get(`/bikes?${q.toString()}`)
            .then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
            .catch(() => { setItems([]); setTotal(0); })
            .finally(() => setLoading(false));
    }, [category, fuel, brand, budget, sort, featuredOnly, budgetMeta.price_min, budgetMeta.price_max]);

    const applyPreset = (preset, remember = true) => {
        const key = preset.id || presetKey(preset);
        setActiveKey(key);
        setCategory(preset.category || "");
        setFuel(preset.fuel_type || "");
        setBrand(preset.brand || "");
        const b = BUDGETS.find((x) => x.price_min === (preset.price_min || "") && x.price_max === (preset.price_max || ""));
        setBudget(b?.value || "");
        setSort(preset.sort || "popularity");
        setFeaturedOnly(Boolean(preset.featured_only));
        if (remember) {
            try { sessionStorage.setItem(STORAGE_KEY, key); } catch { /* ignore */ }
        }
    };

    const browseUrl = () => {
        const q = presetToParams({ category, fuel_type: fuel, brand, price_min: budgetMeta.price_min, price_max: budgetMeta.price_max, sort, featured_only: featuredOnly });
        const s = q.toString();
        return s ? `/bikes?${s}` : "/bikes";
    };

    const clearHomeFilters = () => {
        setCategory("");
        setFuel("");
        setBrand("");
        setBudget("");
        setSort("popularity");
        setFeaturedOnly(false);
        setActiveKey("");
        try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    };

    const forgetPreset = () => {
        setActiveKey("");
        try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    };

    const budgetLabel = BUDGETS.find((b) => b.value === budget)?.label;
    const sortLabel = sort && sort !== "popularity" ? SORTS.find((s) => s.value === sort)?.label : "";
    const canClear = Boolean(category || fuel || brand || budget || sortLabel || featuredOnly);
    const chipStillShowing = chips.some((c) => (c.id || presetKey(c)) === activeKey);
    const hiddenHome = !chipStillShowing && canClear;

    const extraChip = (label, onRemove) => (
        <button type="button" onClick={onRemove} className="shrink-0 inline-flex items-center gap-1 h-6 pl-2 pr-1 rounded-md bg-[#F4F6F8] border border-[#e5e7eb] text-[11px] text-[#0A2540]">
            {label} <X size={11} className="text-[#E31837]" />
        </button>
    );

    const clearButton = (
        <button type="button" onClick={clearHomeFilters} className="shrink-0 whitespace-nowrap text-[11px] text-[#E31837] font-semibold inline-flex items-center gap-1 h-7 px-2">
            <X size={12} /> Clear
        </button>
    );

    const selectionRow = !adjustOpen && hiddenHome ? (
        <div className="mt-1.5 flex items-center gap-1">
            <ScrollCue className="flex items-center gap-1">
                {hiddenHome && (
                    <>
                        {category && extraChip(category, () => { setCategory(""); forgetPreset(); })}
                        {fuel && extraChip(fuel, () => { setFuel(""); forgetPreset(); })}
                        {brand && extraChip(brand, () => { setBrand(""); forgetPreset(); })}
                        {budget && extraChip(budgetLabel, () => { setBudget(""); forgetPreset(); })}
                        {sortLabel && extraChip(sortLabel, () => { setSort("popularity"); forgetPreset(); })}
                        {featuredOnly && extraChip("Deals", () => { setFeaturedOnly(false); forgetPreset(); })}
                    </>
                )}
            </ScrollCue>
            {clearButton}
        </div>
    ) : null;

    const onCompare = (bike) => {
        const result = addToCompare(bike.id);
        if (result.added) toast.success(`${bike.name} added to compare shortlist`);
        else if (result.full) toast.error("Shortlist full (3 bikes). Remove one or go to Compare.");
        else toast.info(`${bike.name} is already in your shortlist`);
    };

    return (
        <section id="inventory" className="py-10 sm:py-14 bg-[#F4F6F8] scroll-mt-28 sm:scroll-mt-32" data-testid="home-bike-finder">
            <div className="container-x">
                <div className="flex items-center justify-between gap-4 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <span className="shrink-0 h-11 w-11 rounded-2xl bg-[#fff0f1] text-[#E31837] inline-flex items-center justify-center">
                            <Bike size={20} strokeWidth={1.75} />
                        </span>
                        <div className="min-w-0">
                            <h2 className="font-display font-bold text-xl sm:text-2xl text-[#0A2540] leading-tight">{pages?.home_finder_title || "Browse bikes"}</h2>
                            <p className="text-[13px] text-[#7a8597] mt-0.5 line-clamp-1">
                                {(pages?.home_finder_subtitle && pages.home_finder_subtitle.length <= 64)
                                    ? pages.home_finder_subtitle
                                    : "Find the perfect bike for your next ride"}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                        <p className="text-sm text-[#7a8597]">{loading ? "…" : `${total} ${total === 1 ? "bike" : "bikes"}`}</p>
                        <span className="h-4 w-px bg-[#e4e7eb]" aria-hidden />
                        <Link
                            to={browseUrl()}
                            className="inline-flex items-center gap-1.5 h-9 pl-3.5 pr-3 rounded-full border border-[#e6e8ec] bg-white text-sm font-medium text-[#0A2540] hover:border-[#E31837] hover:text-[#E31837]"
                            data-testid="home-filter-view-all"
                        >
                            All <ArrowRight size={14} />
                        </Link>
                    </div>
                </div>

                <div className="mb-4 rounded-2xl border border-[#e8eaee] bg-white px-2 py-2 shadow-[0_8px_24px_rgba(10,37,64,0.05)]" data-testid="home-filter-bar">
                    <div className="flex items-center gap-1">
                        <ScrollCue className="flex gap-1.5">
                            {chips.map((c) => {
                                const key = c.id || presetKey(c);
                                return (
                                    <Chip
                                        key={key}
                                        active={activeKey === key}
                                        onClick={() => (activeKey === key && canClear ? clearHomeFilters() : applyPreset(c))}
                                        label={c.label}
                                        count={c.count}
                                        testId={`home-quick-${key}`}
                                    />
                                );
                            })}
                        </ScrollCue>
                        <button
                            type="button"
                            onClick={() => setAdjustOpen((v) => !v)}
                            aria-expanded={adjustOpen}
                            className={`shrink-0 inline-flex items-center gap-1.5 h-9 pl-3 pr-2.5 ml-1 border-l border-[#eef0f3] text-[13px] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E31837] ${
                                adjustOpen ? "text-[#E31837]" : "text-[#0A2540] hover:text-[#E31837]"
                            }`}
                            data-testid="home-filter-toggle"
                        >
                            <SlidersHorizontal size={14} />
                            <span className="sm:hidden">{adjustOpen ? "Hide" : "Filters"}</span>
                            <span className="hidden sm:inline">{adjustOpen ? "Hide" : "More filters"}</span>
                            {hiddenHome && (
                                <span className="min-w-[1.1rem] text-center text-[10px] font-bold rounded bg-[#E31837] text-white px-1">
                                    {[category, fuel, brand, budget, sortLabel, featuredOnly].filter(Boolean).length}
                                </span>
                            )}
                            <ChevronDown size={14} className={`transition-transform ${adjustOpen ? "rotate-180" : ""}`} />
                        </button>
                    </div>
                    {!adjustOpen && selectionRow}
                    {adjustOpen && (
                        <div className="mt-2 pt-2 border-t border-[#eef0f3]">
                            {canClear && (
                                <div className="flex justify-end">
                                    {clearButton}
                                </div>
                            )}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
                            <select className="input input-compact" value={category} onChange={(e) => { setActiveKey(""); setCategory(e.target.value); }}>
                                <option value="">All types</option>
                                <option value="Scooty & EV">Scooty & EV</option>
                                <option value="Motorcycle">Motorcycle</option>
                            </select>
                            <select className="input input-compact" value={fuel} onChange={(e) => { setActiveKey(""); setFuel(e.target.value); }}>
                                <option value="">Any fuel</option>
                                <option value="Petrol">Petrol</option>
                                <option value="Electric">Electric</option>
                            </select>
                            <select className="input input-compact" value={budget} onChange={(e) => { setActiveKey(""); setBudget(e.target.value); }}>
                                {BUDGETS.map((b) => selectOption(b.value, b.label, b.value || "any"))}
                            </select>
                            <select className="input input-compact" value={sort} onChange={(e) => { setActiveKey(""); setSort(e.target.value); }}>
                                {SORTS.map((s) => selectOption(s.value, s.label))}
                            </select>
                            </div>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="py-10 text-center text-[#7a7a84] text-sm">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="bike-card p-8 text-center bg-white" data-testid="home-filter-empty">
                        <p className="text-sm text-[#4a4a52] mb-3">No matches.</p>
                        <Link to="/bikes" className="btn btn-primary btn-sm">Browse all bikes</Link>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="home-filter-results">
                        {items.map((b, i) => (
                            <motion.div key={b.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.15) }}>
                                <BikeCard bike={b} onCompare={onCompare} inCompare={compareIds.includes(b.id)} />
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};

export default HomeBikeFinder;
